import { DataResultParseStream } from '../parsing/DataResultParsed';

export const CHART_DATA_POINT_COLOR_KEY = '__chartColor';

// First 100 colors from Colorcet's precomputed Glasbey categorical palette.
export const DEFAULT_CHART_COLORS = [
  '#D70000',
  '#8C3CFF',
  '#028800',
  '#00ACC7',
  '#98FF00',
  '#FF7FD1',
  '#6C004F',
  '#FFA530',
  '#583B00',
  '#005759',
  '#0000DD',
  '#00FDCF',
  '#A1756A',
  '#BCB7FF',
  '#95B578',
  '#C004B9',
  '#645474',
  '#790000',
  '#0774D8',
  '#FEF590',
  '#004B00',
  '#8F7A00',
  '#FF7266',
  '#EEB9B9',
  '#5E7E66',
  '#9BE4FF',
  '#EC0077',
  '#A67BB9',
  '#5A00A4',
  '#04C600',
  '#9E4B00',
  '#9C3B50',
  '#CBC400',
  '#718298',
  '#00AF8A',
  '#8388FF',
  '#5D373B',
  '#390000',
  '#FDC0FF',
  '#BEE7C0',
  '#DB6D01',
  '#93B8B6',
  '#E452FF',
  '#2F5382',
  '#C46690',
  '#556220',
  '#C59F72',
  '#048287',
  '#69E780',
  '#802790',
  '#6DB4FF',
  '#4E33FF',
  '#86A302',
  '#FE03CB',
  '#C2A6C5',
  '#C55746',
  '#76583D',
  '#016842',
  '#00D6D5',
  '#DAE0FF',
  '#F9FF00',
  '#6A68B0',
  '#C39800',
  '#E1CD9C',
  '#DA96FF',
  '#BB03FD',
  '#925282',
  '#A00073',
  '#579B55',
  '#D38C8F',
  '#374527',
  '#97A5C3',
  '#8E8D5F',
  '#FF4600',
  '#C8FFFA',
  '#AE6DFF',
  '#6ED0A7',
  '#BFFF8C',
  '#8C54B1',
  '#783619',
  '#FFA079',
  '#A9001F',
  '#FF1C45',
  '#5F1123',
  '#679794',
  '#FF5F94',
  '#4C6774',
  '#5392CC',
  '#AA7131',
  '#02CFFE',
  '#00C46C',
  '#61355D',
  '#90D42F',
  '#BFD57C',
  '#5145A2',
  '#4E230C',
  '#7C5A00',
  '#FFCE44',
  '#8302CF',
  '#4DFDFF',
];

export const getChartDataPointColor = (index: number): string =>
  DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length];

export const applyDataPointColors = <T extends { [key: string]: string | number }>(
  data: T[],
  enabled: boolean
): T[] => {
  if (!enabled) {
    return data;
  }

  return data.map(
    (entry: T, index: number): T =>
      ({
        ...entry,
        [CHART_DATA_POINT_COLOR_KEY]: getChartDataPointColor(index),
      } as T)
  );
};

interface ChartVisualizationConfig {
  fill?: string;
  innerRadius?: number;
  name?: string;
  nameKey?: string;
  outerRadius?: number;
  stroke?: string;
  unit?: string;
  colorByDataPoint?: boolean;
  valueType?: 'boolean';
}

export interface ChartSeriesConfig {
  color: string;
  dataKey: string;
  gradientId?: string;
  innerRadius?: number;
  name: string;
  nameKey?: string;
  outerRadius?: number;
  unit?: string;
  colorByDataPoint?: boolean;
  valueType?: 'boolean';
}

interface CreateChartSeriesOptions {
  createGradientId?: (streamIdx: number, vizIdx: number) => string;
  getColor?: (
    visualization: ChartVisualizationConfig,
    streamIdx: number,
    vizIdx: number
  ) => string | undefined;
}

export const createChartSeries = (
  streams: DataResultParseStream[],
  options: CreateChartSeriesOptions = {}
): ChartSeriesConfig[] => {
  return streams.reduce(
    (series: ChartSeriesConfig[], stream: DataResultParseStream, streamIdx: number) => {
      stream.visualization.forEach(
        (visualization: ChartVisualizationConfig, vizIdx: number): void => {
          const dataKey = `value_${streamIdx}_${vizIdx}`;
          const fallbackColor =
            DEFAULT_CHART_COLORS[(streamIdx + vizIdx) % DEFAULT_CHART_COLORS.length];
          const color =
            options.getColor?.(visualization, streamIdx, vizIdx) ??
            visualization.stroke ??
            visualization.fill ??
            fallbackColor;

          series.push({
            color,
            dataKey,
            gradientId: options.createGradientId?.(streamIdx, vizIdx),
            innerRadius: visualization.innerRadius,
            name: visualization.name ?? dataKey,
            nameKey: visualization.nameKey,
            outerRadius: visualization.outerRadius,
            unit: visualization.unit,
            colorByDataPoint: visualization.colorByDataPoint,
            valueType: visualization.valueType,
          });
        }
      );

      return series;
    },
    []
  );
};
